"use client";

import { cn } from "@/lib/cn";
import { PageHeader } from "@/components/page-header";
import type { BreadcrumbCrumb } from "@/components/breadcrumbs";
import type { PageBarRemove } from "@/components/page-bar";
import type { ActionMenuItem } from "@/components/ui/action-menu";

/**
 * Canonical operator-page shell.
 *
 * Every operator surface (Issues, Inbox, Patches, Knowledge, Projects,
 * Workspaces, Runs, Follow-ups) is structurally the same:
 *
 *   <OperatorPage>
 *     <OperatorPage.Header eyebrow title subtitle actions />
 *     [optional intermediate rows — stat cards, filter strip]
 *     <OperatorPage.Content>{table or list}</OperatorPage.Content>
 *     [optional <OperatorPage.SplitView> with <DetailPane>]
 *   </OperatorPage>
 *
 * Before this shell, eight pages picked their own padding (p-4 sm:p-6
 * vs p-6), their own vertical rhythm (space-y-3 / 4 / 5 / 6), their
 * own background (grid-bg vs none), and Issues + Inbox each rolled
 * a bespoke split-pane layout. The shell folds all of that into
 * tokens so every page renders identically without each route
 * having to re-derive the chrome.
 *
 * Visual treatment is deliberately unchanged — the canonical values
 * picked here (p-4 sm:p-6, space-y-4) match what 5/8 pages already
 * shipped. The two outliers (Issues used space-y-3 for very dense
 * triage; Patches/Runs/Follow-ups used p-6 + space-y-6) collapse
 * onto the same rhythm so the "this is one product" feel returns.
 */

const PAGE_PADDING = "p-4 sm:p-6";
const PAGE_RHYTHM  = "space-y-4";
const PAGE_BG      = "grid-bg min-h-full";

export type OperatorPageProps = {
  children: React.ReactNode;
  className?: string;
  /**
   * Caps the content width so ultra-wide monitors don't stretch
   * tables across 3000px. Defaults to none — operator tables benefit
   * from the full viewport. Pages that genuinely want a narrower
   * column (settings forms, docs) can opt in to "lg" / "xl".
   */
  maxWidth?: "full" | "xl" | "2xl";
};

/**
 * Page wrapper. Sets the canonical padding, vertical rhythm, and
 * grid background. Children are direct page sections — header,
 * stat row, toolbar, content — and inherit the rhythm.
 */
export function OperatorPage({
  children,
  className,
  maxWidth = "full",
}: OperatorPageProps) {
  return (
    <div
      className={cn(
        PAGE_BG,
        PAGE_PADDING,
        PAGE_RHYTHM,
        maxWidth === "xl" && "max-w-7xl mx-auto",
        maxWidth === "2xl" && "max-w-[1600px] mx-auto",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Page header — `<PageHeader>` re-exported with consistent prop
 * names. New pages should reach for `OperatorPage.Header` so the
 * top spacing + eyebrow + title + actions cluster never drifts.
 */
function Header({
  eyebrow, title, subtitle, actions, breadcrumb, breadcrumbAside, breadcrumbMenu, remove,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  breadcrumb?: BreadcrumbCrumb[] | false;
  /** Right edge of the sticky breadcrumb row — in practice `<DetailPager>`. */
  breadcrumbAside?: React.ReactNode;
  /** Extra record-level items for the breadcrumb row's `⋯` menu. */
  breadcrumbMenu?: ActionMenuItem[];
  /** Standard confirmed delete for the record this page shows. */
  remove?: PageBarRemove;
}) {
  return (
    <PageHeader
      eyebrow={eyebrow}
      title={title}
      subtitle={subtitle}
      actions={actions}
      breadcrumb={breadcrumb}
      breadcrumbAside={breadcrumbAside}
      breadcrumbMenu={breadcrumbMenu}
      remove={remove}
    />
  );
}

/**
 * Sticky toolbar slot for pages that DON'T render a `<DataTable>`.
 *
 * The DataTable already manages its own sticky toolbar via the
 * `stickyToolbar` prop and measures the toolbar height to offset
 * the column header. Pages with a table should pass their toolbar
 * through `<DataTable toolbar={…} stickyToolbar />` — not this slot.
 *
 * Use `<OperatorPage.Toolbar>` only on pages with a custom layout
 * (e.g. Follow-ups card list, future settings list views) where
 * the toolbar would otherwise float above non-sticky content.
 */
function Toolbar({
  children,
  sticky = false,
  className,
}: {
  children: React.ReactNode;
  sticky?: boolean;
  className?: string;
}) {
  return (
    <div
      // Stacks below the page's sticky breadcrumb bar (`--page-bar-h`, 0 on
      // pages without one) rather than sliding underneath it.
      style={sticky ? { top: "var(--page-bar-h, 0px)" } : undefined}
      className={cn(
        sticky && "sticky z-20 sticky-chrome -mx-4 sm:-mx-6 px-4 sm:px-6 py-2",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Main content area — neutral wrapper. Most pages don't need this
 * (they render a `<DataTable>` directly as a child of OperatorPage),
 * but it exists so pages that have multiple content blocks can name
 * the primary one explicitly.
 */
function Content({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn("min-w-0", className)}>{children}</div>;
}

/**
 * Optional left rail for category trees / saved-view sidebars.
 * Currently unused by any operator page — included for spec
 * symmetry so future routes have a documented slot.
 */
function Sidebar({
  children,
  width = 260,
  className,
}: {
  children: React.ReactNode;
  width?: number;
  className?: string;
}) {
  return (
    <aside
      style={{ width: `${width}px` }}
      className={cn(
        "shrink-0 border-r border-line-subtle bg-bg-raised/30 self-stretch overflow-y-auto subtle-scroll",
        className,
      )}
    >
      {children}
    </aside>
  );
}

/**
 * Footer slot — fixed at the bottom of the page, used for global
 * actions or status. Currently unused; spec'd so future pages
 * have one canonical place to put per-page footers.
 */
function Footer({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 pt-3 border-t border-line-subtle text-[11.5px] text-fg-muted",
        className,
      )}
    >
      {children}
    </div>
  );
}

OperatorPage.Header = Header;
OperatorPage.Toolbar = Toolbar;
OperatorPage.Content = Content;
OperatorPage.Sidebar = Sidebar;
OperatorPage.Footer = Footer;

// Re-export the canonical pane primitives so pages can extend without
// importing from two places. `OperatorPage.SplitView` was retired in
// favour of the standalone `<SplitView>` primitive (`./split-view`),
// which supports both detail-aside (Issues/Patches/Follow-ups) and
// list-aside (Inbox) layouts.
export { PANE_DEFAULTS, PANE_MOBILE_BREAKPOINT } from "@/lib/use-resizable-pane";
export { useResizablePane } from "@/lib/use-resizable-pane";
